
import React, { useRef, useEffect, useCallback } from 'react';
import { AppProvider, useAppContext } from './contexts/AppContext';
import SimulationCanvas, { SimulationCanvasHandle } from './components/SimulationCanvas';
import Controls from './components/Controls';
import CalibrationModal from './components/CalibrationModal';
import HelpModal from './components/HelpModal';
import SimulationReport from './components/SimulationReport';
import AgentBehaviorModal from './components/AgentBehaviorModal';
import { SimStatsHUD } from './components/HUD/SimStatsHUD';
import { useSimulation } from './hooks/useSimulation';
import { toPng } from 'html-to-image';
import { isLayoutPayload, normalizeLayoutPayload } from './utils/layoutImport';
import { ArchitectClipboard, createArchitectClipboard, instantiateClipboard } from './utils/architectClipboard';

const SimulationContainer: React.FC = () => {
  const { state, dispatch } = useAppContext();
  const { spawnAgent, triggerMCI } = useSimulation(state, dispatch);
  const canvasRef = useRef<SimulationCanvasHandle>(null);
  const clipboardRef = useRef<ArchitectClipboard | null>(null);
  const pasteCountRef = useRef(0);

  const copySelection = useCallback(() => {
    if (state.selectedItems.length === 0) return;
    clipboardRef.current = createArchitectClipboard(state, state.selectedItems);
    pasteCountRef.current = 0;
  }, [state]);

  const pasteClipboard = useCallback(() => {
    const clipboard = clipboardRef.current;
    const bounds = canvasRef.current?.getContentRect();
    if (!clipboard || !bounds) return;
    pasteCountRef.current += 1;
    dispatch({ type: 'PASTE_ITEMS', payload: instantiateClipboard(clipboard, pasteCountRef.current * 20, bounds) });
  }, [dispatch]);

  const duplicateSelection = useCallback(() => {
    if (state.selectedItems.length === 0) return;
    clipboardRef.current = createArchitectClipboard(state, state.selectedItems);
    pasteCountRef.current = 1;
    const bounds = canvasRef.current?.getContentRect();
    if (bounds) {
      dispatch({ type: 'PASTE_ITEMS', payload: instantiateClipboard(clipboardRef.current, 20, bounds) });
    }
  }, [dispatch, state]);

  // Keyboard Shortcuts Handler
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.target instanceof HTMLInputElement || e.target instanceof HTMLTextAreaElement) return;

      if (e.key === 'Escape') {
        dispatch({ type: 'SET_EDIT_MODE', payload: 'SELECT' });
        dispatch({ type: 'SET_SELECTION', payload: [] });
        dispatch({ type: 'SET_REPORT_OPEN', payload: false });
      }

      if (!e.ctrlKey && !e.metaKey && !e.altKey && e.key.toLowerCase() === 'h') {
        dispatch({ type: 'SET_EDIT_MODE', payload: 'PAN' });
      }

      const modifier = e.ctrlKey || e.metaKey;
      const key = e.key.toLowerCase();
      if (modifier && key === 'z') {
        e.preventDefault();
        dispatch({ type: e.shiftKey ? 'REDO' : 'UNDO' });
      } else if (modifier && key === 'y') {
        e.preventDefault();
        dispatch({ type: 'REDO' });
      } else if (modifier && key === 'a') {
        e.preventDefault();
        dispatch({ type: 'SET_SELECTION', payload: [
          ...state.nodes.map(item => ({ kind: 'NODE' as const, id: item.id })),
          ...state.zones.map(item => ({ kind: 'ZONE' as const, id: item.id })),
          ...state.walls.map(item => ({ kind: 'WALL' as const, id: item.id })),
          ...state.paths.map(item => ({ kind: 'PATH' as const, id: item.id })),
          ...state.icons.map(item => ({ kind: 'ICON' as const, id: item.id }))
        ] });
      } else if (modifier && (key === 'c' || key === 'd')) {
        if (state.selectedItems.length === 0) return;
        e.preventDefault();
        if (key === 'c') copySelection();
        else duplicateSelection();
      } else if (modifier && key === 'v') {
        e.preventDefault();
        pasteClipboard();
      } else if (e.key === 'Delete' || e.key === 'Backspace') {
        if (state.selectedPathPoint) {
          dispatch({ type: 'DELETE_PATH_POINT', payload: state.selectedPathPoint });
        } else if (state.selectedItems.length > 0) {
          dispatch({ type: 'DELETE_SELECTION', payload: state.selectedItems });
        }
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [copySelection, dispatch, duplicateSelection, pasteClipboard, state]);

  const handleBgUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
    const input = e.currentTarget;
    const file = e.target.files?.[0];
    if (!file) return;

    if (!file.type.startsWith('image/')) {
      alert("Prašome pasirinkti paveikslėlio failą.");
      input.value = '';
      return;
    }

    const reader = new FileReader();
    reader.onerror = () => alert("Klaida skaitant failą.");
    reader.onload = (readerEvent) => {
      const content = readerEvent.target?.result as string;
      if (content) {
        dispatch({ type: 'SET_FLOOR_PLAN', payload: content });
      }
    };
    reader.readAsDataURL(file);
    input.value = '';
  };

  const handleExport = async () => {
    const node = canvasRef.current?.getExportNode();
    const contentRect = canvasRef.current?.getContentRect();
    if (!node || !contentRect) return;
    
    try {
      const maxDimension = 8192;
      const maxPixels = 16_000_000;
      const pixelRatio = Math.max(0.1, Math.min(
        2,
        maxDimension / contentRect.width,
        maxDimension / contentRect.height,
        Math.sqrt(maxPixels / (contentRect.width * contentRect.height))
      ));
      const dataUrl = await toPng(node, { 
        quality: 1, 
        pixelRatio,
        width: contentRect.width,
        height: contentRect.height,
        backgroundColor: '#020617'
      });
      
      const link = document.createElement('a');
      link.download = `smps-export-${Date.now()}.png`;
      link.href = dataUrl;
      link.click();
    } catch (err) {
      console.error("Export failed", err);
      alert("Eksportavimas nepavyko. Bandykite dar kartą.");
    }
  };

  const handleSave = () => {
    try {
      const data = {
        nodes: state.nodes,
        zones: state.zones,
        walls: state.walls,
        paths: state.paths,
        icons: state.icons,
        pixelsPerMeter: state.pixelsPerMeter,
        floorPlanImage: state.floorPlanImage,
        mapOpacity: state.mapOpacity,
        mapBrightness: state.mapBrightness,
        mapContrast: state.mapContrast,
        simConfig: state.simConfig,
        version: "2.5.0-STABLE"
      };
      const blob = new Blob([JSON.stringify(data, null, 2)], { type: 'application/json' });
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `smps-layout-${Date.now()}.json`;
      link.click();
      setTimeout(() => URL.revokeObjectURL(url), 100);
    } catch (e) {
      alert("Nepavyko išsaugoti brėžinio.");
    }
  };

  const handleLoad = (e: React.ChangeEvent<HTMLInputElement>) => {
    const input = e.currentTarget;
    const file = e.target.files?.[0];
    if (!file) return;

    const reader = new FileReader();
    reader.onerror = () => alert("Klaida skaitant failą.");
    reader.onload = (event) => {
      try {
        const text = event.target?.result as string;
        const content = normalizeLayoutPayload(JSON.parse(text));
        
        if (isLayoutPayload(content)) {
          dispatch({ type: 'LOAD_LAYOUT', payload: content });
        } else {
          throw new Error("Invalid format");
        }
      } catch (err) {
        alert("Klaida įkeliant failą: neteisingas formatas.");
      }
    };
    reader.readAsText(file);
    input.value = '';
  };

  return (
    <div className="relative h-screen w-screen overflow-hidden font-sans select-none">
      <SimulationCanvas 
        ref={canvasRef}
        width={2000} 
        height={1500} 
        onBgUpload={handleBgUpload} 
      />
      
      <SimStatsHUD />
      
      {state.editMode === 'CALIBRATE_SCALE' && state.calibrationPoints.length === 2 && (
        <CalibrationModal 
          pixelDistance={Math.sqrt(Math.pow(state.calibrationPoints[1].x - state.calibrationPoints[0].x, 2) + Math.pow(state.calibrationPoints[1].y - state.calibrationPoints[0].y, 2))}
          onConfirm={(m) => {
             const p1 = state.calibrationPoints[0]; const p2 = state.calibrationPoints[1];
             const dist = Math.sqrt(Math.pow(p2.x - p1.x, 2) + Math.pow(p2.y - p1.y, 2));
             dispatch({ type: 'SET_CALIBRATION', payload: { scale: dist / m, points: [] } });
             dispatch({ type: 'SET_EDIT_MODE', payload: 'SELECT' });
          }}
          onCancel={() => {
            dispatch({ type: 'SET_CALIBRATION', payload: { points: [] } });
            dispatch({ type: 'SET_EDIT_MODE', payload: 'SELECT' });
          }}
        />
      )}

      <HelpModal 
        isOpen={state.isHelpOpen} 
        initialTab={state.helpTab}
        onClose={() => dispatch({ type: 'SET_HELP_OPEN', payload: { open: false } })} 
      />

      <SimulationReport />
      <AgentBehaviorModal />

      <Controls 
        onSpawnAgent={spawnAgent}
        onTriggerMCI={triggerMCI}
        onUploadImage={handleBgUpload} 
        onSave={handleSave}
        onLoad={handleLoad}
        onExport={handleExport}
        onCopy={copySelection}
        onPaste={pasteClipboard}
        onDuplicate={duplicateSelection}
      />
    </div>
  );
};

const App: React.FC = () => (
  <AppProvider>
    <SimulationContainer />
  </AppProvider>
);

export default App;
